import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Lock, ArrowRight } from "lucide-react";

const errText = (d, f) => (typeof d === "string" ? d : f);

const DEFAULT_ADMIN_EMAIL = "info@giremodeling.net";

export default function AdminSetup() {
  const { applySession } = useAuth();
  const navigate = useNavigate();

  // status: loading | available | already_setup
  const [status, setStatus] = useState("loading");
  const [form, setForm] = useState({
    name: "",
    email: DEFAULT_ADMIN_EMAIL,
    password: "",
    confirm: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api
      .get("/auth/setup-status")
      .then((r) => setStatus(r.data.needs_setup ? "available" : "already_setup"))
      .catch(() => setStatus("already_setup"));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/setup-admin", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      applySession(data);
      toast.success("Primary administrator created");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      // If backend now says setup is completed (409), flip UI to disabled state
      if (err.response?.status === 409) {
        setStatus("already_setup");
      }
      toast.error(errText(detail, "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <div
        data-testid="admin-setup-loading"
        className="min-h-screen bg-[#050505] flex items-center justify-center text-gold font-serif text-xl"
      >
        …
      </div>
    );
  }

  if (status === "already_setup") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div
          data-testid="admin-setup-disabled"
          className="w-full max-w-md border border-white/10 bg-[#0a0a0a] p-8 text-center"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Lock className="text-gold" />
            <span className="font-serif text-2xl">
              <span className="gold-gradient-text">GI</span>{" "}
              <span className="silver-gradient-text">Admin</span>
            </span>
          </div>
          <h1 className="font-serif text-3xl text-white mb-3">Setup already completed</h1>
          <p className="text-white/50 font-body text-sm mb-8">
            The primary administrator has already been created. This screen is disabled.
            New administrators can only be added from the admin dashboard.
          </p>
          <Link
            to="/login"
            data-testid="admin-setup-go-login"
            className="inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-widest px-6 py-3 hover:bg-white transition-colors"
          >
            Go to Sign In <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck className="text-gold" />
          <span className="font-serif text-2xl">
            <span className="gold-gradient-text">GI</span>{" "}
            <span className="silver-gradient-text">Admin</span>
          </span>
        </div>
        <h1 className="font-serif text-3xl md:text-4xl mb-2 text-white">
          Create First Admin Account
        </h1>
        <p className="text-white/50 font-body text-sm mb-8">
          Set up the primary administrator for GI Remodeling. This screen is disabled after
          the first admin is created. Additional administrators can only be added from the
          admin dashboard.
        </p>

        <form data-testid="admin-setup-form" onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">
              Full name
            </label>
            <input
              data-testid="admin-setup-name"
              required
              autoComplete="name"
              placeholder="e.g. Gerson Iraheta"
              value={form.name}
              onChange={set("name")}
              className="editorial-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">
              Email address
            </label>
            <input
              data-testid="admin-setup-email"
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={set("email")}
              className="editorial-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">
              Password
            </label>
            <input
              data-testid="admin-setup-password"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="At least 8 characters"
              value={form.password}
              onChange={set("password")}
              className="editorial-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">
              Confirm password
            </label>
            <input
              data-testid="admin-setup-confirm"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="Re-enter password"
              value={form.confirm}
              onChange={set("confirm")}
              className="editorial-input"
            />
            {form.confirm && form.confirm !== form.password && (
              <p
                data-testid="admin-setup-mismatch"
                className="text-red-400 text-xs mt-2"
              >
                Passwords do not match
              </p>
            )}
          </div>

          <button
            data-testid="admin-setup-submit"
            disabled={submitting}
            className="w-full bg-gold text-black text-sm font-semibold uppercase tracking-widest py-4 hover:bg-white transition-colors disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create Admin Account"}
          </button>
        </form>

        <p className="text-white/30 text-xs mt-6 text-center">
          Already have an admin account?{" "}
          <Link to="/login" className="text-gold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
